import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDynamicConfig } from '@statsig/react-bindings';
import { QUERY_KEYS } from '@viome/viome-base/src/api/config';
import { useGetInitData } from '@viome/viome-base/src/usecase/Bootup/BootupQuery';
import {
  ReviewStatus,
  type ReviewStatusType,
} from '@viome/viome-base/src/usecase/Misc/MiscModel';
import { useUpdateReviewStatusMutation } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { useUserDetails } from '@/hooks/useUserDetails';
import { fetchTrustpilotInvitationLink } from '@/api/trustpilot';
import { EventTracker } from '@/utils';
import { KEYS, LocalStorage } from '@utils/LocalStorage';
import environment from '@config/environment';

const FORCE_SHOW_PARAM = 'showReview';
const TRUSTPILOT_PUBLIC_REVIEW_URL =
  'https://www.trustpilot.com/review/viome.com';
const INVITATION_LINK_ERROR_TEXT =
  'Something went wrong. Please try again later.';

/** The `?showReview=true` override must never be usable against production. */
const isForceShowAllowed = (): boolean =>
  import.meta.env.DEV ||
  (environment.ENVIRONMENT || '').toLowerCase() !== 'production';

const hasForceShowParam = (): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get(FORCE_SHOW_PARAM) ===
      'true'
    );
  } catch {
    return false;
  }
};

const getStoredUser = () => {
  try {
    const raw = LocalStorage.getItem(KEYS.USER);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data ?? null;
  } catch {
    return null;
  }
};

export const useTrustpilotReviewPopup = () => {
  const queryClient = useQueryClient();
  const { data: initData, isLoading } = useGetInitData();
  const { mutate: updateReviewStatus, isPending: isUpdatingReviewStatus } =
    useUpdateReviewStatusMutation();
  const user = useUserDetails();
  const enableTrustpilotReview = useDynamicConfig('public_config').get(
    'enable_trustpilot_review',
    false,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasTrackedSeen = useRef(false);
  const isPersistingStatus = useRef(false);
  const hasDismissed = useRef(false);

  const showFromApi = initData?.review_config?.show_review_popup === true;
  const forceShow = isForceShowAllowed() && hasForceShowParam();
  const shouldShow = enableTrustpilotReview && showFromApi;

  const persistReviewStatus = useCallback(
    (status: ReviewStatusType) => {
      if (forceShow || isPersistingStatus.current) return;

      isPersistingStatus.current = true;
      updateReviewStatus(status, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INIT_DATA] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INIT_DATA_V2] });
        },
        onSettled: () => {
          isPersistingStatus.current = false;
        },
      });
    },
    [forceShow, queryClient, updateReviewStatus],
  );

  useEffect(() => {
    if (forceShow) {
      setIsOpen(true);
      return;
    }
    if (isLoading) return;

    if (shouldShow) {
      setIsOpen(true);
      hasDismissed.current = false;
      if (!hasTrackedSeen.current) {
        hasTrackedSeen.current = true;
        EventTracker.trackEvent('home', 'home_trustpilot_review_popup_seen');
      }
    } else {
      setIsOpen(false);
      hasTrackedSeen.current = false;
    }
  }, [isLoading, shouldShow, forceShow]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setErrorMessage(null);
    if (hasDismissed.current) return;
    hasDismissed.current = true;
    persistReviewStatus(ReviewStatus.Skipped);
    EventTracker.trackEvent('home', 'home_trustpilot_review_popup_dismissed');
  }, [persistReviewStatus]);

  const openReview = useCallback(async () => {
    const storedUser = getStoredUser();
    const email = (user?.email || storedUser?.email || '').trim();
    const name =
      (user?.fullName || storedUser?.fullName || '').trim() ||
      [
        user?.firstName || storedUser?.firstName,
        user?.lastName || storedUser?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      (email ? email.split('@')[0] : '');

    const reviewOrderId = initData?.review_config?.order_id;
    const orderId =
      reviewOrderId != null ? String(reviewOrderId) : 'viome-app-user';

    if (!email || !name) {
      persistReviewStatus(ReviewStatus.Reviewed);
      EventTracker.trackEvent('home', 'home_trustpilot_review_cta_clicked');
      window.open(
        TRUSTPILOT_PUBLIC_REVIEW_URL,
        '_blank',
        'noopener,noreferrer',
      );
      setIsOpen(false);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const url = await fetchTrustpilotInvitationLink({
        order_id: orderId,
        name,
        email,
      });
      persistReviewStatus(ReviewStatus.Reviewed);
      EventTracker.trackEvent('home', 'home_trustpilot_review_cta_clicked');
      window.open(url, '_blank', 'noopener,noreferrer');
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to fetch Trustpilot invitation link:', error);
      setErrorMessage(INVITATION_LINK_ERROR_TEXT);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    initData?.review_config?.order_id,
    persistReviewStatus,
    user,
  ]);

  return {
    isOpen,
    isSubmitting: isSubmitting || isUpdatingReviewStatus,
    errorMessage,
    dismiss,
    openReview,
  };
};
